import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Typography,
  Box,
} from '@mui/material';
import { PlatformSchedule, ScheduleVariants, pastScheduleEntries } from './PlatformSchedule';
import { useIsMobile } from '../hooks/useIsMobile';

interface Props {
  open: boolean;
  /** Platforms the post targets, so the timing advice can name them. */
  platforms?: string[];
  /** The post being scheduled, so the same-day note does not report it against itself. */
  postId?: string;
  /** Current scheduled time, if the post already has one. */
  initial?: string | null;
  /** Current per-platform times, if the post already has any. */
  initialVariants?: ScheduleVariants | null;
  /** True when the post is already scheduled, so the copy says "reschedule". */
  rescheduling?: boolean;
  busy?: boolean;
  error?: string;
  onClose: () => void;
  /**
   * null clears the schedule and returns the post to a draft. `variants` carries the
   * per-platform times; an empty object means every network publishes at `isoDate`.
   */
  onConfirm: (isoDate: string | null, variants: ScheduleVariants) => void;
}

/**
 * Pick a time for a post that already exists.
 *
 * Separate from the composer because scheduling from Post History or the Calendar should
 * not require re-entering content and platforms — and because a PUT that omitted
 * `platforms` used to wipe them.
 */
export const SchedulePostDialog: React.FC<Props> = ({
  open,
  platforms = [],
  postId,
  initial,
  initialVariants,
  rescheduling = false,
  busy = false,
  error,
  onClose,
  onConfirm,
}) => {
  const isMobile = useIsMobile();
  const [when, setWhen] = useState<Date | null>(initial ? new Date(initial) : null);
  const [variants, setVariants] = useState<ScheduleVariants>(initialVariants || {});

  // The server refuses a past time; saying so here avoids a pointless round trip.
  const inPast = when !== null && when.getTime() <= Date.now();
  // Per-network times are checked too — the base time being fine says nothing about them.
  const pastPlatforms = pastScheduleEntries(platforms, variants);

  return (
    <Dialog fullScreen={isMobile} open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{rescheduling ? 'Reschedule post' : 'Schedule post'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The scheduler publishes within about a minute of the time you choose. Times are in
          your own timezone.
        </Typography>

        <PlatformSchedule
          platforms={platforms}
          base={when}
          variants={variants}
          onBaseChange={setWhen}
          onVariantsChange={setVariants}
          disabled={busy}
          postId={postId}
        />

        {inPast && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            That time has passed. Choose a future time, or use Publish now.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {/* Clearing the schedule returns the post to Idea Staging, which is the drafts view */}
        <Box>
          {rescheduling && (
            <Button color="inherit" disabled={busy} onClick={() => onConfirm(null, {})}>
              Move to Idea Staging
            </Button>
          )}
        </Box>
        <Box>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy || !when || inPast || pastPlatforms.length > 0}
            onClick={() => when && onConfirm(when.toISOString(), variants)}
          >
            {rescheduling ? 'Reschedule' : 'Schedule'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
